/**
 * PB-FND-75 — Audio field plugin (registry adapter).
 * Extracted from pb-field-runtime; wired via createAudioAdapter(deps).
 */

export const AUDIO_MIC_PATH_D =
  'M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z';

export function buildAudioMicSvg(options) {
  const opts = options || {};
  const width = opts.width != null ? opts.width : 16;
  const height = opts.height != null ? opts.height : 16;
  const className = opts.className != null ? opts.className : 'pb-icon-mic';
  const classAttr = className ? ' class="' + className + '"' : '';
  return (
    '<svg' +
    classAttr +
    ' width="' +
    width +
    '" height="' +
    height +
    '" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="' +
    AUDIO_MIC_PATH_D +
    '"/></svg>'
  );
}

export const AUDIO_MIC_SVG = buildAudioMicSvg();

const AUDIO_PLAY_SVG =
  '<svg class="pb-lt-icon-play" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
const AUDIO_PAUSE_SVG =
  '<svg class="pb-lt-icon-pause" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" hidden><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>';
const AUDIO_TRASH_SVG =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>';

/**
 * @param {object} deps — shared field-runtime helpers
 */
export function createAudioAdapter(deps) {
  const {
    resolveEsc,
    resolveSelections,
    settingsToString,
    resolveRuntimeParseSettings,
    renderControlShell,
    renderImageColumn,
    delegateInitStep,
    bindAudioWrapInteractions,
  } = deps;

  return {
    id: 'audio',
    matches(field) {
      return String((field && field.type) || '').includes('Audio');
    },
    renderStep(ctx) {
      const field = ctx.field;
      const esc = resolveEsc(ctx);
      const settingsRaw = field.settings;
      const settingsStr = settingsToString(settingsRaw);
      const parseFn = resolveRuntimeParseSettings(ctx);
      const audioSettings = parseFn(settingsRaw);
      const audioRequired = !!(audioSettings && audioSettings.required);
      const reqAttr = audioRequired ? 'true' : 'false';
      const existingVal = resolveSelections(ctx)[field.label] || '';
      const hasVal = !!existingVal;
      const initialDisplay = hasVal ? 'none' : 'flex';
      const reviewDisplay = hasVal ? 'flex' : 'none';
      const innerHtml =
        '<div class="pb-audio-wrap" data-label="' +
        esc(field.label) +
        '" data-settings="' +
        esc(settingsStr) +
        '">' +
        '<input type="hidden" class="builder-required" data-label="' +
        esc(field.label) +
        '" data-required="' +
        reqAttr +
        '" data-valid="' +
        (hasVal ? 'true' : 'false') +
        '" value="' +
        esc(existingVal) +
        '">' +
        '<div class="pb-audio-container">' +
        '<div class="pb-audio-state pb-state-initial" style="display:' +
        initialDisplay +
        ';">' +
        '<button type="button" class="pb-audio-btn-main" aria-label="Record">' +
        AUDIO_MIC_SVG +
        'Record</button></div>' +
        '<div class="pb-audio-state pb-state-recording" style="display:none;">' +
        '<span class="pb-audio-timer">00:00</span></div>' +
        '<div class="pb-audio-state pb-state-review" style="display:' +
        reviewDisplay +
        ';">' +
        '<div class="pb-audio-playback">' +
        '<div class="pb-audio-playback-bar">' +
        '<button type="button" class="pb-audio-btn-play" aria-label="Play">' +
        AUDIO_PLAY_SVG +
        AUDIO_PAUSE_SVG +
        '</button>' +
        '<canvas class="pb-audio-wave-canvas pb-audio-wave-playback" height="32"></canvas>' +
        '</div>' +
        '<button type="button" class="pb-audio-btn-delete" aria-label="Delete recording">' +
        AUDIO_TRASH_SVG +
        '</button></div></div>' +
        '<div class="pb-audio-message" style="display:none;"></div>' +
        '</div></div>';
      const visRaw = String(field.visual || '').trim();
      return {
        controlHtml: renderControlShell(field, innerHtml, ctx, { useQuestionHighlight: !visRaw }),
        imageHtml: renderImageColumn(field, ctx),
        stepAttrs: '',
        extraClasses: !visRaw ? 'pb-step-hide-question' : '',
      };
    },
    initStep(stepEl) {
      delegateInitStep(stepEl, '.pb-audio-wrap', function (wrap) {
        bindAudioWrapInteractions(wrap);
      });
    },
    validateStep(stepEl) {
      const wrap = stepEl && stepEl.querySelector('.pb-audio-wrap');
      if (!wrap) return true;
      const hidden = wrap.querySelector('input.builder-required[data-label]');
      if (!hidden || hidden.dataset.required !== 'true') return true;
      return hidden.dataset.valid === 'true';
    },
    hydrateStep(stepEl, ctx) {
      const field = ctx.field;
      const val = resolveSelections(ctx)[field.label] || '';
      delegateInitStep(stepEl, '.pb-audio-wrap', function (wrap) {
        bindAudioWrapInteractions(wrap);
        if (typeof window !== 'undefined' && window.pbAudioRecorder?.hydrate && val) {
          window.pbAudioRecorder.hydrate(wrap, val);
        }
      });
    },
    flushStep(stepEl, ctx) {
      const field = ctx.field;
      const hidden = stepEl && stepEl.querySelector('.pb-audio-wrap input.builder-required[data-label]');
      if (!hidden || !ctx.selections) return;
      ctx.selections[field.label] = hidden.value;
    },
  };
}
